import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListSubheader,
} from "@mui/material";
import { groupBy } from "lodash-es";
import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TagGroupInfo } from "./types";

export const MergeTagAccordion: FC<{
  active: boolean;
  activeIndex: number | null;
  tagInfo: TagGroupInfo;
  section: string;
  defaultExpanded: boolean;
  onSelect: (tag: string) => void;
}> = ({ active, activeIndex, tagInfo, section, defaultExpanded, onSelect }) => {
  const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [expanded, setExpanded] = useState(() => {
    if (tagInfo.groupTags.length === 0) return false;

    return defaultExpanded;
  });

  useEffect(() => {
    const isActive =
      defaultExpanded || (active && activeIndex !== null && tagInfo.groupTags.length > 0);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExpanded(isActive);
    if (isActive && activeIndex) {
      buttonRefs.current[activeIndex]?.scrollIntoView({ block: "center" });
    }
  }, [active, tagInfo.groupTags, activeIndex, defaultExpanded]);

  const subGroups = useMemo(
    () => groupBy(tagInfo.groupTags, (x) => x.category_key ?? ""),
    [tagInfo.groupTags],
  );
  return (
    <Accordion expanded={tagInfo.groupTags.length > 0 && expanded}>
      <AccordionSummary
        data-testid={`accordion-${section}`}
        sx={{
          backgroundColor: active ? "action.hover" : undefined,
          minWidth: 300,
        }}
        onClick={() => setExpanded(expanded ? !expanded : tagInfo.groupTags.length > 0)}
        expandIcon={tagInfo.groupTags.length > 0 && <ExpandMore />}
      >
        {tagInfo.title}
      </AccordionSummary>
      <AccordionDetails sx={{ my: 0, p: 0 }}>
        <List data-testid={`${section}-tag-list`} sx={{ overflow: "auto" }}>
          {Object.entries(subGroups).map(([subGroup, subGroupTags]) => {
            return (
              <Box key={`${section}-tag-list-${subGroup}`}>
                {tagInfo.subtitle?.(subGroup) && (
                  <ListSubheader>{tagInfo.subtitle(subGroup)}</ListSubheader>
                )}
                {subGroupTags.map((tag) => {
                  const index = tagInfo.groupTags.indexOf(tag);
                  const key = tag.category_key
                    ? `${tag.category_key}.${tag.field_key}`
                    : tag.field_key;
                  return (
                    <ListItem
                      key={key}
                      sx={{ p: 0 }}
                      value={tag.field_name}
                      onClick={() => onSelect(key)}
                    >
                      <ListItemButton
                        sx={{ py: 1 }}
                        data-testid={key}
                        selected={activeIndex !== null && active && activeIndex === index}
                        ref={(el) => {
                          buttonRefs.current[index] = el;
                        }}
                      >
                        {tag.field_name}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </Box>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
